import jsCookie from 'js-cookie'
import _ from 'lodash'
import {createContext, FC, PropsWithChildren, useContext, useEffect, useState} from 'react'

import {ABTestConfig, resolveABTestConfig} from '@/utils/ab-testing/config'
import {writeABTestConfigCookie} from '@/utils/ab-testing/cookie'
import {getCurrentSearchParams} from '@/utils/browser/location'
import {abTestConfigCookieName} from '@/utils/constants/http'

const ABTestContext = createContext<{
  config: ABTestConfig
  setABTest: (name: string, value: 0 | 1) => void
}>({
  config: {},
  setABTest: _.identity,
})
export const useABTestConfig = () => useContext(ABTestContext)

/**
 * Assigns the visitor's variants in the browser, after the page has loaded - see "A/B assignment"
 * in docs/visitor-preferences.md. No server render can know them: the roll involves
 * `Math.random()`, and a page carries nothing per visitor, so every page renders without a variant
 * and picks it up here.
 */
export const ABTestProvider: FC<PropsWithChildren> = ({children}) => {
  const [config, setConfig] = useState<ABTestConfig>({})

  useEffect(() => {
    const resolved = resolveABTestConfig(
      jsCookie.get(abTestConfigCookieName),
      getCurrentSearchParams(),
    )
    setConfig(resolved)
    // Rewritten on every page load, so an assignment lapses only after six months without a visit
    writeABTestConfigCookie(resolved)
  }, [])

  const setABTest = (name: string, value: 0 | 1) => {
    const nextConfig = {...config, [name]: value}

    setConfig(nextConfig)
    writeABTestConfigCookie(nextConfig)
  }

  return <ABTestContext.Provider value={{config, setABTest}}>{children}</ABTestContext.Provider>
}
